import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import debug from 'debug';
import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { useController } from '@/hooks/useController';
import { useSessionOverview } from '@/hooks/useSessionOverview';
import { cn } from '@/lib/utils';
import { DeliverInfo } from '@/newComponents/ChatEvent/DeliverInfo';
import { AgentResponseEvent } from '@/newComponents/Message/AgentResponseEvent';
import { BrainResponseEvent } from '@/newComponents/Message/BrainResponseEvent/BrainResponseEvent';
import { isLastMemberLeftEvent, isWidgetExpandedState, chatEventsState, overviewSelector, } from '@/state/session';
import { WaitingTime } from '../WaitingTime';
import { Attachments } from './Attachment';
import { Swimmlane } from './Swimmlane';
const log = debug('moveo:chat-event');
export const ChatEvent = memo(({ event }) => {
    var _a;
    const { t } = useTranslation();
    const isExpanded = useRecoilValue(isWidgetExpandedState);
    const { waitingTime, queuePosition } = useRecoilValue(overviewSelector);
    const chatEvents = useRecoilValue(chatEventsState);
    const { startSessionOverview } = useSessionOverview();
    const { notifyMemberLeave } = useController();
    useEffect(() => {
        if (event.event_type === 'member_leave') {
            notifyMemberLeave();
            const isFirstMemberLeave = chatEvents.filter((e) => e.event_type === 'member_leave').length === 1;
            if (isFirstMemberLeave) {
                startSessionOverview();
            }
        }
    }, [
        event.event_id,
        event.event_type,
        chatEvents,
        notifyMemberLeave,
        startSessionOverview,
    ]);
    if (event.event_type === 'user_message') {
        const baseCss = 'text-text-primary-foreground dark:text-text-primary-foreground relative ml-auto w-fit rounded-xl px-4 py-3';
        return (_jsxs("li", { "data-event-type": "user_message", className: cn('ml-auto mt-6 flex max-w-xs flex-col', {
                'max-w-md': isExpanded,
            }), children: [event.text && (_jsx("p", { className: cn(baseCss, 'min-w-20 break-words bg-accent text-left dark:text-foreground', {
                        'text-center': ((_a = event.text) === null || _a === void 0 ? void 0 : _a.length) && event.text.length < 5,
                    }), children: event.text })), event.attachments &&
                    event.attachments.length > 0 &&
                    event.attachments.map((a) => (_jsx("div", { className: cn(baseCss, 'mt-2', {
                            'bg-accent': a.type !== 'image',
                        }), children: _jsx(Attachments, { id: a.id, filename: a.filename, title: a.title, url: a.url, type: a.type }) }, a.id))), _jsx(DeliverInfo, { chatEvent: event })] }));
    }
    else if (event.event_type === 'agent_message') {
        return _jsx(AgentResponseEvent, { event: event });
    }
    else if (event.event_type === 'member_join') {
        return (_jsx(Swimmlane, { children: _jsx("div", { "data-event-type": "member_join", children: t('events.member_join', { 0: t(event.author_name) }) }) }));
    }
    else if (event.event_type === 'member_leave') {
        if (waitingTime !== null && isLastMemberLeftEvent(event.event_id)) {
            return (_jsx(Swimmlane, { children: _jsx(WaitingTime, { waitingTime: waitingTime, queuePosition: queuePosition }) }));
        }
        return (_jsx(Swimmlane, { children: _jsx("div", { "data-event-type": "member_leave", children: t('events.member_leave', { 0: t(event.author_name) }) }) }));
    }
    else if (event.event_type === 'brain_message') {
        return _jsx(BrainResponseEvent, { event: event });
    }
    else {
        log('Ignoring event: %O', event);
    }
    return null;
});
ChatEvent.displayName = 'ChatEvent';
